import { motion } from "framer-motion";
import logoSvg from "@/assets/logo.svg";

interface AnimatedCreativePreviewProps {
  headline: string;
}

export default function AnimatedCreativePreview({ headline }: AnimatedCreativePreviewProps) {
  return (
    <div className="aspect-square rounded-lg overflow-hidden border border-white/[0.06] bg-[#0a0f1e] relative">
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            "radial-gradient(circle at 20% 30%, rgba(20,184,166,0.08) 0%, transparent 60%)",
            "radial-gradient(circle at 80% 70%, rgba(20,184,166,0.12) 0%, transparent 60%)",
            "radial-gradient(circle at 50% 20%, rgba(20,184,166,0.06) 0%, transparent 60%)",
            "radial-gradient(circle at 20% 30%, rgba(20,184,166,0.08) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Animated border frame */}
      <div className="absolute inset-4">
        <motion.div
          className="absolute inset-0 border border-white/[0.06] rounded"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        />

        {/* Animated corner accents */}
        {[
          { top: 0, left: 0, borderClass: "border-l-2 border-t-2" },
          { top: 0, right: 0, borderClass: "border-r-2 border-t-2" },
          { bottom: 0, left: 0, borderClass: "border-l-2 border-b-2" },
          { bottom: 0, right: 0, borderClass: "border-r-2 border-b-2" },
        ].map((corner, i) => (
          <motion.div
            key={i}
            className={`absolute w-8 h-8 border-teal-500 ${corner.borderClass}`}
            style={{
              top: corner.top !== undefined ? corner.top : undefined,
              bottom: corner.bottom !== undefined ? corner.bottom : undefined,
              left: corner.left !== undefined ? corner.left : undefined,
              right: corner.right !== undefined ? corner.right : undefined,
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.4, ease: "easeOut" }}
          />
        ))}
      </div>

      {/* Animated scanning line */}
      <motion.div
        className="absolute left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-teal-500/30 to-transparent"
        animate={{ top: ["10%", "90%", "10%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating particles */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full bg-teal-500/20"
          style={{ left: `${20 + i * 15}%`, top: `${30 + (i % 3) * 20}%` }}
          animate={{
            y: [0, -10, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.4,
          }}
        />
      ))}

      {/* Logo */}
      <motion.img
        src={logoSvg}
        alt="AdV3nture"
        className="absolute top-5 left-6 h-6 w-auto"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      />

      {/* Headline */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
        <motion.p
          className="text-white font-bold text-center text-lg sm:text-xl leading-tight max-w-[80%]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
        >
          {headline}
        </motion.p>
        <motion.div
          className="w-10 h-0.5 bg-teal-500 mt-3"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1, duration: 0.4, ease: "easeOut" }}
        />
      </div>

      {/* Pulsing accent ring */}
      <motion.div
        className="absolute bottom-12 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full border border-teal-500/10"
        animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* URL */}
      <motion.span
        className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/30 text-[9px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        adv3nture.io
      </motion.span>
    </div>
  );
}
